import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { SubscriptionContextType } from '@/types/subscription';
import { useSubscriptionData } from '@/hooks/useSubscriptionData';
import { useTrialManagement } from '@/hooks/useTrialManagement';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  
  // Use custom hooks for separation of concerns
  const {
    subscribed,
    productId,
    subscriptionEnd,
    trialEnd,
    cancelAtPeriodEnd,
    cancelAt,
    stripeCustomerId,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
  } = useSubscriptionData();
  
  const { isTrialActive, trialDaysRemaining } = useTrialManagement(trialEnd);

  // Initial session check
  useEffect(() => {
    let mounted = true;

    console.log('[INIT] Checking for existing session...');
    
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      
      console.log('[INIT] Session found:', !!session, 'User:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        console.log('[INIT] Calling checkSubscription for existing session...');
        // Non-blocking: Don't await to speed up initial render
        checkSubscription(session.user.id);
      }
    });

    return () => {
      mounted = false;
    };
  }, [checkSubscription]);

  // Auth state change listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AUTH] Event:', event, 'User:', session?.user?.id);
        
        // Handle sign out
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          return;
        }
        
        // Handle sign in - only if we don't have a user yet (to catch initial login)
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          setSession(session);
          setUser(session.user);
          // Check subscription on login
          checkSubscription(session.user.id);
          return;
        }
        
        // Handle token refresh - silently update session but don't re-check subscription
        if (event === 'TOKEN_REFRESHED' && session?.user) {
          setSession(session);
          setUser(session.user);
          // Don't check subscription on token refresh to avoid flickering
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  // Note: Realtime listener removed to prevent infinite loop
  // The subscription is checked on login and after checkout/portal actions

  return (
    <SubscriptionContext.Provider
      value={{
        user,
        session,
        loading,
        subscribed,
        productId,
        subscriptionEnd,
        trialEnd,
        cancelAtPeriodEnd,
        cancelAt,
        stripeCustomerId,
        isTrialActive,
        trialDaysRemaining,
        checkSubscription,
        createCheckout,
        openCustomerPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}